// eslint-disable-next-line import/no-extraneous-dependencies
import 'react-image-lightbox/style.css';

import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import Lightbox from 'react-image-lightbox';
import styled from 'styled-components';

import { DraggedCardWrapper } from '../DraggedCardWrapper';
import { GalleryContainer, GalleryItem } from './LightboxGallery.styled';

interface LightboxGalleryProps {
  draggedCards: { id: string; order: number }[];
  title?: string;
  isPendingOrderChange?: boolean;
  hoveredIndex?: number | null;
  handleDragOver?: (e: React.DragEvent) => void;
  handleDragEnter?: (index: number) => void;
  handleDrop?: (e: React.DragEvent, dropIndex: number) => void;
  handleDragStart?: (e: React.DragEvent, index: number) => void;
  handleDragEnd?: () => void;
  isDragging?: boolean;
  /** opcjonalnie, aby zablokować skoki layoutu */
  itemAspectRatio?: number; // np. 3/2
  skeletonHeight?: number; // alternatywnie stała wysokość skeletonu
}

const SkeletonOverlay = styled.div<{ $height?: number; $ratio?: number }>`
  position: absolute;
  inset: 0;
  display: block;
  background: linear-gradient(90deg, #eee 25%, #f5f5f5 37%, #eee 63%);
  background-size: 400% 100%;
  animation: shimmer 1.2s infinite;
  border-radius: 6px;

  /* jeśli przekazano stały height – ustawiamy go,
     jeśli nie – użyjemy aspect-ratio aby utrzymać miejsce */
  height: ${({ $height }) => ($height ? `${$height}px` : 'auto')};
  aspect-ratio: ${({ $ratio }) => ($ratio ? `${$ratio}` : 'auto')};

  @keyframes shimmer {
    0% { background-position: 100% 0; }
    100% { background-position: 0 0; }
  }
`;

const ItemWrapper = styled.div`
  position: relative;
  width: 100%;
`;

export const LightboxGallery: React.FC<LightboxGalleryProps> = ({
  draggedCards,
  title,
  isPendingOrderChange,
  hoveredIndex,
  handleDragOver,
  handleDragEnter,
  handleDrop,
  handleDragStart,
  handleDragEnd,
  isDragging,
  itemAspectRatio,
  skeletonHeight,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);

  // loaded map: id -> boolean
  const [loaded, setLoaded] = useState<Record<string, boolean>>({});

  // gdy lista się zmienia, zaznacz nowe obrazki jako niezaładowane
  useEffect(() => {
    setLoaded((prev) => {
      const next = { ...prev };
      draggedCards.forEach(({ id }) => {
        if (!(id in next)) next[id] = false;
      });
      // opcjonalnie usuń z mapy rzeczy, których już nie ma
      Object.keys(next).forEach((k) => {
        if (!draggedCards.some((c) => c.id === k)) delete next[k];
      });
      return next;
    });
  }, [draggedCards]);

  const markLoaded = useCallback((id: string) => {
    setLoaded((prev) => (prev[id] ? prev : { ...prev, [id]: true }));
  }, []);

  useLayoutEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => {
        window.dispatchEvent(new Event('resize'));
      });
    }
  }, [isOpen]);

  if (!draggedCards || draggedCards.length === 0) return null;

  const openAt = (index: number) => {
    setPhotoIndex(index);
    setIsOpen(true);
  };

  const handleKeyOpen: React.KeyboardEventHandler = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const target = e.currentTarget as HTMLElement & { dataset?: { index?: string } };
      const idx = Number(target?.dataset?.index ?? 0);
      openAt(idx);
    }
  };

  return (
    <>
      {title && <h3 className="font-normal">{title}</h3>}
      <GalleryContainer>
        {draggedCards.map((item, index) => {
          const isItemLoaded = !!loaded[item.id];

          return (
            <DraggedCardWrapper
              key={item.id ?? item.order}
              isHovered={hoveredIndex === index}
              onDragOver={handleDragOver}
              onDragEnter={() => handleDragEnter && handleDragEnter(index)}
              onDrop={(e) => handleDrop && handleDrop(e, index)}
              onDragStart={(e) => handleDragStart && handleDragStart(e, index)}
              onDragEnd={handleDragEnd}
              isDragging={!isPendingOrderChange && isDragging}
            >
              <ItemWrapper>
                {!isItemLoaded && (
                <SkeletonOverlay
                  aria-hidden="true"
                  $height={skeletonHeight}
                  $ratio={itemAspectRatio}
                />
                )}

                <GalleryItem
                  src={item.id}
                  alt={`Miniatura ${index + 1}`}
                        /* eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role */
                  role="button"
                  tabIndex={0}
                  data-index={index}
                  style={{
                    cursor: 'pointer',
                    opacity: isItemLoaded ? 1 : 0,
                    transition: 'opacity 180ms ease-in',
                    display: 'block',
                  }}
                  onLoad={() => markLoaded(item.id)}
                  onError={() => markLoaded(item.id)} // ukryj skeleton również przy błędzie
                  onClick={() => openAt(index)}
                  onKeyDown={handleKeyOpen}
                  draggable={false}
                />
              </ItemWrapper>
            </DraggedCardWrapper>
          );
        })}
      </GalleryContainer>

      {isOpen && (
        <Lightbox
          mainSrc={draggedCards[photoIndex].id || ''}
          nextSrc={draggedCards[(photoIndex + 1) % draggedCards.length].id || ''}
          prevSrc={draggedCards[(photoIndex + draggedCards.length - 1) % draggedCards.length].id || ''}
          onCloseRequest={() => setIsOpen(false)}
          onMovePrevRequest={() => setPhotoIndex((photoIndex + draggedCards.length - 1) % draggedCards.length)}
          onMoveNextRequest={() => setPhotoIndex((photoIndex + 1) % draggedCards.length)}
          animationDisabled
        />
      )}
    </>
  );
};
